import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Flex,
  Progress,
  Row,
  Spin,
  Tag,
  Typography,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { getCorpus } from '@/api/ai-chat/corpus'
import type { CorpusData } from '@/api/ai-chat/types'
import { OUTCOME_COLORS } from './utils'

const { Title, Text } = Typography

const BATCH_STATUS_COLORS: Record<string, string> = {
  in_progress: 'warning',
  ended: 'success',
  error: 'error',
}

export const CorpusPage: React.FC = () => {
  const [corpus, setCorpus] = useState<CorpusData | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getCorpus()
      setCorpus(data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <Spin spinning={loading}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Corpus
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load}>
          Refresh
        </Button>
      </Flex>

      {corpus && (
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card
              title="Embeddings"
              extra={
                <Text type="secondary">
                  {(corpus.total ?? 0).toLocaleString()} total
                </Text>
              }
            >
              {(corpus.by_outcome?.length ?? 0) === 0 ? (
                <Text type="secondary">No embeddings yet</Text>
              ) : (
                (corpus.by_outcome ?? []).map((r) => {
                  const pct = corpus.total
                    ? Math.round((Number(r.count) / corpus.total) * 100)
                    : 0
                  return (
                    <Flex
                      key={r.outcome}
                      align="center"
                      gap={8}
                      style={{ marginBottom: 10 }}
                    >
                      <Flex
                        align="center"
                        gap={6}
                        style={{ width: 110, flexShrink: 0 }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: OUTCOME_COLORS[r.outcome] ?? '#ccc',
                            flexShrink: 0,
                          }}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {r.outcome}
                        </Text>
                      </Flex>
                      <div style={{ flex: 1 }}>
                        <Progress
                          percent={pct}
                          showInfo={false}
                          strokeColor={OUTCOME_COLORS[r.outcome] ?? '#ccc'}
                          size="small"
                        />
                      </div>
                      <Text
                        strong
                        style={{ width: 60, textAlign: 'right', fontSize: 12 }}
                      >
                        {Number(r.count).toLocaleString()}
                      </Text>
                    </Flex>
                  )
                })
              )}
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card title="Batch Jobs">
              {(corpus.batches?.length ?? 0) === 0 ? (
                <Text type="secondary">No active batches</Text>
              ) : (
                (corpus.batches ?? []).map((b) => {
                  const total = b.total || 1
                  const pct = Math.round(((b.succeeded || 0) / total) * 100)
                  return (
                    <Flex
                      key={b.id}
                      align="center"
                      gap={8}
                      style={{
                        paddingBottom: 10,
                        marginBottom: 10,
                        borderBottom: '1px solid #e2e5ea',
                      }}
                    >
                      <Text
                        code
                        style={{
                          fontSize: 11,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {b.id}
                      </Text>
                      <div style={{ width: 120 }}>
                        <Progress percent={pct} size="small" showInfo={false} />
                      </div>
                      <Text
                        type="secondary"
                        style={{ fontSize: 11, width: 36, textAlign: 'right' }}
                      >
                        {pct}%
                      </Text>
                      <Tag color={BATCH_STATUS_COLORS[b.status] ?? 'default'}>
                        {b.status}
                      </Tag>
                    </Flex>
                  )
                })
              )}
            </Card>
          </Col>
        </Row>
      )}
    </Spin>
  )
}
